import type { ReactElement } from 'react';
import { type ButtonHTMLAttributes, forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';

import { Spinner } from '../Spinner';

import styles from './styles/Button.module.scss';

export interface ButtonBaseProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  /**
   * Toggle state, on the element rather than in the variant string: `aria-pressed` for assistive
   * technology and `data-state` for CSS and tests. Style it as `.<emphasis>[data-state='on']` — an
   * emphasis value that means "selected" is the bug this replaces (NGF-7095).
   */
  pressed?: boolean;
}

export interface ButtonProps extends ButtonBaseProps, VariantProps<typeof buttonVariants> {
  isFullWidth?: boolean;
  isLoading?: boolean;
  loadingText?: ReactElement | string;
  rounded?: boolean;
}

export const buttonVariants = cva(styles.button, {
  variants: {
    variant: {
      default: styles.primary,
      ghost: styles.ghost,
      ghostBrand: styles.ghostBrand,
      secondary: styles.secondary,
      tertiary: styles.tertiary,
      outline: styles.outline,
      surface: styles.surface,
    },
    size: {
      'xx-small': styles.xxSmallSize,
      'x-small': styles.xSmallSize,
      small: styles.smallSize,
      default: styles.defaultSize,
      large: styles.largeSize,
      icon: styles.iconSize,
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

/**
 * Behaviour without a skin: the reset, `asChild`, disabled handling and the shared focus ring.
 * Compose it when a control brings its own appearance; reach for `Button` when you want the
 * design-system button. Adding a decorative declaration to `.buttonBase` defeats the point.
 */
export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(
  ({ asChild = false, className, disabled, pressed, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    // Spread last, and only when `pressed` was given: Radix passes its own `data-state` through
    // `asChild` triggers, and an unconditional `data-state={undefined}` would delete it.
    const state = pressed === undefined ? undefined : { 'aria-pressed': pressed, 'data-state': pressed ? 'on' : 'off' };

    return (
      <Comp
        className={clsx(styles.buttonBase, className)}
        data-cxy-name="button-base"
        disabled={disabled}
        ref={ref}
        {...props}
        {...state}
      />
    );
  },
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ asChild, isLoading, loadingText, variant, ...props }, ref) => (
    <>
      {isLoading ? (
        <BaseButton {...props} aria-busy={isLoading} disabled={isLoading} ref={ref} variant={variant}>
          <>
            {loadingText ? loadingText : 'Loading'}
            <Spinner size="s" variant={variant === 'secondary' ? 'dark' : 'light'} />
          </>
        </BaseButton>
      ) : (
        <BaseButton {...props} asChild={asChild} ref={ref} variant={variant} />
      )}
    </>
  ),
);

export const BaseButton = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, isFullWidth, rounded, size, variant, ...props }, ref) => (
    <ButtonBase
      className={clsx(
        buttonVariants({ variant, size }),
        { [styles.isFullWidth]: isFullWidth, [styles.rounded]: rounded },
        className,
      )}
      data-cxy-name="button"
      data-testid="button-component"
      ref={ref}
      {...props}
    />
  ),
);

Button.displayName = 'Button';
ButtonBase.displayName = 'ButtonBase';
